import { ChangeEvent, DragEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Clock3,
  Download,
  FileSpreadsheet,
  FileText,
  FileType,
  FolderOpen,
  Minus,
  MonitorSmartphone,
  Plus,
  RotateCcw,
  Share2,
  Trash2,
  X
} from "lucide-react";
import { DocumentViewer } from "./components/DocumentViewer";
import {
  ACCEPTED_FILE_TYPES,
  FileKind,
  formatBytes,
  formatDate,
  getFileKind,
  getKindLabel
} from "./lib/fileTypes";
import {
  clearStoredFiles,
  consumeSharedBatch,
  deleteStoredFile,
  fileFromRecord,
  listStoredFiles,
  saveFiles,
  saveSharedFiles,
  StoredFileRecord,
  touchStoredFile
} from "./lib/storage";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const MIN_ZOOM = 0.55;
const MAX_ZOOM = 2.25;
const ZOOM_STEP = 0.1;

export default function App() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [records, setRecords] = useState<StoredFileRecord[]>([]);
  const [activeRecord, setActiveRecord] = useState<StoredFileRecord | null>(null);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [toast, setToast] = useState("");
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);

  const activeFile = useMemo(() => (activeRecord ? fileFromRecord(activeRecord) : null), [activeRecord]);
  const activeKind = activeFile ? getFileKind(activeFile) : null;

  const refreshRecords = useCallback(async () => {
    const nextRecords = await listStoredFiles();
    setRecords(nextRecords);
    return nextRecords;
  }, []);

  const flash = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  }, []);

  const openFiles = useCallback(
    async (files: File[], source: "local" | "handler" | "share" = "local") => {
      const filtered = files.filter((file) => file.size > 0);
      if (filtered.length === 0) {
        flash("열 수 있는 파일이 없습니다.");
        return;
      }

      const saved = await saveFiles(filtered, source);
      setActiveRecord(saved[0]);
      setZoom(1);
      await refreshRecords();
      flash(filtered.length === 1 ? "파일을 열었습니다." : `${filtered.length}개 파일을 추가했습니다.`);
    },
    [flash, refreshRecords]
  );

  useEffect(() => {
    refreshRecords().then((stored) => {
      if (!activeRecord && stored[0]) {
        setActiveRecord(stored[0]);
      }
    });
  }, [activeRecord, refreshRecords]);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js?v=20260623-android-install", { updateViaCache: "none" }).catch(() => {
        flash("오프라인 캐시를 준비하지 못했습니다.");
      });
    }
  }, [flash]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const batchId = params.get("share");
    const shouldOpenPicker = params.get("action") === "open";

    if (batchId) {
      consumeSharedBatch(batchId)
        .then(async (batch) => {
          if (!batch || batch.files.length === 0) {
            flash("공유된 파일을 찾지 못했습니다.");
            return;
          }

          const saved = await saveSharedFiles(batch.files);
          setActiveRecord(saved[0]);
          setZoom(1);
          await refreshRecords();
          flash(batch.files.length === 1 ? "공유 파일을 열었습니다." : `공유 파일 ${batch.files.length}개를 열었습니다.`);
        })
        .finally(() => {
          window.history.replaceState({}, "", "/");
        });
    } else if (params.get("share-error")) {
      flash("공유 파일을 받지 못했습니다.");
      window.history.replaceState({}, "", "/");
    } else if (shouldOpenPicker) {
      window.history.replaceState({}, "", "/");
      window.setTimeout(() => inputRef.current?.click(), 250);
    }
  }, [flash, refreshRecords]);

  useEffect(() => {
    window.launchQueue?.setConsumer((launchParams) => {
      Promise.all(launchParams.files.map((handle) => handle.getFile()))
        .then((files) => openFiles(files, "handler"))
        .catch(() => flash("파일 핸들러에서 문서를 열지 못했습니다."));
    });
  }, [flash, openFiles]);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    const displayMode = window.matchMedia("(display-mode: standalone)");
    const updateStandalone = () => {
      const navigatorStandalone = Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
      setIsStandalone(displayMode.matches || navigatorStandalone);
    };

    updateStandalone();
    displayMode.addEventListener("change", updateStandalone);
    return () => displayMode.removeEventListener("change", updateStandalone);
  }, []);

  const handleInput = async (event: ChangeEvent<HTMLInputElement>) => {
    await openFiles(Array.from(event.target.files || []), "local");
    event.target.value = "";
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    await openFiles(Array.from(event.dataTransfer.files || []), "local");
  };

  const selectRecord = async (record: StoredFileRecord) => {
    const updated = await touchStoredFile(record);
    setActiveRecord(updated);
    setZoom(1);
    await refreshRecords();
  };

  const removeRecord = async (record: StoredFileRecord) => {
    await deleteStoredFile(record.id);
    const nextRecords = await refreshRecords();
    if (activeRecord?.id === record.id) {
      setActiveRecord(nextRecords[0] || null);
    }
    flash("기록에서 삭제했습니다.");
  };

  const clearAll = async () => {
    await clearStoredFiles();
    setRecords([]);
    setActiveRecord(null);
    flash("최근 기록을 비웠습니다.");
  };

  const runInstallPrompt = async () => {
    if (!installPrompt) {
      flash("브라우저 메뉴에서 홈 화면에 추가할 수 있습니다.");
      return;
    }
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const showInstallButton = !isStandalone;

  const adjustZoom = (direction: "in" | "out" | "reset") => {
    setZoom((current) => {
      if (direction === "reset") return 1;
      const next = current + (direction === "in" ? ZOOM_STEP : -ZOOM_STEP);
      return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(next.toFixed(2))));
    });
  };

  return (
    <div
      className={isDragging ? "app-shell is-dragging" : "app-shell"}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) {
          setIsDragging(false);
        }
      }}
      onDrop={handleDrop}
    >
      <input ref={inputRef} className="visually-hidden" type="file" accept={ACCEPTED_FILE_TYPES} multiple onChange={handleInput} />

      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <img src="/icons/icon-192.png" alt="" width="34" height="34" />
          </div>
          <div>
            <strong>Yviewer</strong>
            <span>office.ycrious.com</span>
          </div>
        </div>

        <div className="sidebar-actions">
          <button className="primary-button" type="button" onClick={() => inputRef.current?.click()}>
            <FolderOpen size={17} />
            <span>파일 열기</span>
          </button>
          {showInstallButton ? (
            <button className="icon-button" type="button" onClick={runInstallPrompt} aria-label="앱 설치" title="앱 설치">
              <Download size={17} />
            </button>
          ) : null}
        </div>

        <div className="recent-heading">
          <span>
            <Clock3 size={15} />
            최근 기록
          </span>
          {records.length > 0 ? (
            <button className="ghost-button" type="button" onClick={clearAll}>
              비우기
            </button>
          ) : null}
        </div>

        <div className="recent-list">
          {records.length === 0 ? <div className="empty-recent">아직 열린 문서가 없습니다.</div> : null}
          {records.map((record) => {
            const kind = getFileKind(record);
            return (
              <button
                key={record.id}
                className={activeRecord?.id === record.id ? "recent-item is-active" : "recent-item"}
                type="button"
                onClick={() => selectRecord(record)}
              >
                <FileBadge kind={kind} />
                <span className="recent-text">
                  <strong>{record.name}</strong>
                  <small>
                    {getKindLabel(kind)} · {formatBytes(record.size)} · {formatDate(record.openedAt)}
                  </small>
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div className="mobile-brand">
            <img className="mobile-brand-icon" src="/icons/icon-192.png" alt="" width="22" height="22" />
            <strong>Yviewer</strong>
          </div>

          {activeRecord ? (
            <div className="file-title">
              <FileBadge kind={activeKind || "unknown"} />
              <div>
                <strong>{activeRecord.name}</strong>
                <span>
                  {formatBytes(activeRecord.size)} · {formatDate(activeRecord.openedAt)}
                </span>
              </div>
            </div>
          ) : (
            <div className="file-title muted">
              <MonitorSmartphone size={18} />
              <div>
                <strong>문서 작업 공간</strong>
                <span>PDF · Word · Excel · PowerPoint</span>
              </div>
            </div>
          )}

          <div className="toolbar">
            <button className="icon-button" type="button" onClick={() => inputRef.current?.click()} aria-label="파일 열기" title="파일 열기">
              <FolderOpen size={17} />
            </button>
            {showInstallButton ? (
              <button className="secondary-button install-main-button" type="button" onClick={runInstallPrompt} aria-label="앱 설치">
                <Download size={17} />
                <span>앱 설치</span>
              </button>
            ) : null}
            <div className="zoom-control" aria-label="확대 축소">
              <button type="button" onClick={() => adjustZoom("out")} aria-label="축소" title="축소">
                <Minus size={15} />
              </button>
              <button type="button" onClick={() => adjustZoom("reset")} aria-label="확대 초기화" title="확대 초기화">
                {Math.round(zoom * 100)}%
              </button>
              <button type="button" onClick={() => adjustZoom("in")} aria-label="확대" title="확대">
                <Plus size={15} />
              </button>
            </div>
            {activeRecord ? (
              <button
                className="icon-button"
                type="button"
                onClick={() => removeRecord(activeRecord)}
                aria-label="기록 삭제"
                title="기록 삭제"
              >
                <Trash2 size={17} />
              </button>
            ) : null}
          </div>
        </header>

        <section className="document-surface">
          {activeFile ? (
            <DocumentViewer file={activeFile} zoom={zoom} />
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <FolderOpen size={30} />
              </div>
              <h1>파일을 선택하세요</h1>
              <div className="empty-actions">
                <button className="primary-button" type="button" onClick={() => inputRef.current?.click()}>
                  <FolderOpen size={17} />
                  <span>파일 열기</span>
                </button>
                <button className="secondary-button" type="button" onClick={() => flash("설치 후 파일 앱의 공유 메뉴에서 선택할 수 있습니다.")}>
                  <Share2 size={17} />
                  <span>공유로 열기</span>
                </button>
              </div>
              <div className="format-row" aria-label="지원 형식">
                <span>PDF</span>
                <span>DOCX</span>
                <span>XLSX</span>
                <span>PPTX</span>
                <span>TXT</span>
                <span>IMG</span>
              </div>
            </div>
          )}
        </section>
      </main>

      {isDragging ? (
        <div className="drop-overlay">
          <FolderOpen size={34} />
          <strong>여기에 놓기</strong>
        </div>
      ) : null}

      {toast ? (
        <div className="toast" role="status">
          <span>{toast}</span>
          <button type="button" onClick={() => setToast("")} aria-label="닫기">
            <X size={15} />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function FileBadge({ kind }: { kind: FileKind }) {
  const icon = (() => {
    if (kind === "sheet") return <FileSpreadsheet size={16} />;
    if (kind === "pptx" || kind === "legacy-ppt") return <FileType size={16} />;
    return <FileText size={16} />;
  })();

  return <span className={`file-badge kind-${kind}`}>{icon}</span>;
}
